// Users Service - Realtime Database operations
import { ref, get, update, remove, set, push } from 'firebase/database'
import { database } from './firebase'
import bcrypt from 'bcryptjs'
import {
  HIGH_IMPACT_DELETIONS_DISABLED,
  getHighImpactDeletionBlock,
} from '@/utils/highImpactDeletionGuard'
import {
  buildUserAuditEntry,
  computeUserChanges,
  normalizeUserForAudit,
  validateUserActor,
} from '@/utils/userAudit'

// Helper para obtener timestamp en milisegundos
const getTimestamp = () => Date.now()
const STORES_BASE_PATH = 'projects/superkomprasBackoffice/stores'
const USERS_BASE_PATH = 'projects/superkomprasBackoffice/users'
const USER_DELETES_PATH = 'projects/superkomprasBackoffice/userDeletes'

/**
 * Append an audit event to a user's auditLog.
 * @param {string} uid
 * @param {string} action
 * @param {Object} actor
 * @param {Object} [options]
 * @returns {Promise<Object>}
 */
const appendUserAuditEvent = async (uid, action, actor, options = {}) => {
  const actorValidation = validateUserActor(actor)
  if (!actorValidation.valid) {
    return {
      success: false,
      error: actorValidation.error,
    }
  }

  const normalizedActor = actorValidation.actor
  const now = getTimestamp()
  const auditLogRef = push(ref(database, `${USERS_BASE_PATH}/${uid}/auditLog`))
  const entry = buildUserAuditEntry(
    action,
    normalizedActor,
    options.changes ?? null,
    options.snapshot ?? null,
    now,
    options.meta ?? null
  )

  await update(ref(database, `${USERS_BASE_PATH}/${uid}`), {
    updatedAt: now,
    updatedBy: normalizedActor,
    [`auditLog/${auditLogRef.key}`]: entry,
  })

  return { success: true }
}

/**
 * Persist deletion record before removing user node from RTDB.
 * @param {string} uid
 * @param {Object} userData
 * @param {Object} actor
 * @returns {Promise<Object>}
 */
const writeUserDeleteLog = async (uid, userData, actor) => {
  const actorValidation = validateUserActor(actor)
  if (!actorValidation.valid) {
    return {
      success: false,
      error: actorValidation.error,
    }
  }

  const now = getTimestamp()
  const deleteLogRef = push(ref(database, USER_DELETES_PATH))

  await set(deleteLogRef, {
    uid,
    email: String(userData.email || '').trim(),
    deletedAt: now,
    deletedBy: actorValidation.actor,
    snapshot: normalizeUserForAudit(userData),
    authDisabled: false,
    authDisablePending: true,
  })

  return { success: true }
}

/**
 * Get user data by UID from Realtime Database
 * @param {string} uid - User UID
 * @returns {Promise<Object>} User data or null if not found
 */
export const getUserByUID = async uid => {
  try {
    const userRef = ref(database, `projects/superkomprasBackoffice/users/${uid}`)
    const snapshot = await get(userRef)

    if (snapshot.exists()) {
      return {
        success: true,
        data: {
          uid: uid,
          ...snapshot.val(),
        },
      }
    } else {
      return {
        success: false,
        error: 'Usuario no encontrado en la base de datos',
        data: null,
      }
    }
  } catch (error) {
    console.error('Error in getUserByUID:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener datos del usuario',
      data: null,
    }
  }
}

/**
 * Get all users from Realtime Database
 * @returns {Promise<Object>} All users data
 */
export const getAllUsers = async () => {
  try {
    const usersRef = ref(database, 'projects/superkomprasBackoffice/users')
    const snapshot = await get(usersRef)

    if (snapshot.exists()) {
      const usersData = snapshot.val()
      // Convertir objeto a array y agregar el uid a cada usuario
      const usersArray = Object.keys(usersData).map(uid => ({
        uid: uid,
        ...usersData[uid],
      }))

      return {
        success: true,
        data: usersArray,
      }
    } else {
      return {
        success: true,
        data: [],
      }
    }
  } catch (error) {
    console.error('Error in getAllUsers:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener usuarios',
      data: [],
    }
  }
}

/**
 * Validate user status and role
 * @param {Object} userData - User data from database
 * @returns {Object} Validation result
 */
export const validateUserStatusAndRole = userData => {
  if (!userData) {
    console.warn('[validateUserStatusAndRole] userData missing')
    return {
      valid: false,
      reason: 'invalid',
      internalDetail: 'userData missing',
    }
  }

  const uid = userData.uid || 'unknown'

  // Validar que existe el status
  const status = userData.status
  if (!status) {
    console.warn('[validateUserStatusAndRole] missing status', { uid })
    return {
      valid: false,
      reason: 'inactive',
      internalDetail: 'missing status',
    }
  }

  // Validar que el status sea "activo" o "Activo"
  const statusLower = String(status).toLowerCase()
  if (statusLower !== 'activo') {
    console.warn('[validateUserStatusAndRole] non-active status', { uid, status })
    return {
      valid: false,
      reason: 'inactive',
      internalDetail: `status=${status}`,
    }
  }

  // Validar que existe el rol
  const rol = userData.rol
  if (!rol) {
    console.warn('[validateUserStatusAndRole] missing rol', { uid })
    return {
      valid: false,
      reason: 'invalid_role',
      internalDetail: 'missing rol',
    }
  }

  // Validar que el rol sea uno de los permitidos
  const rolesPermitidos = [
    'Administrador',
    'Soporte',
    'Servicio a Domicilio',
    'Transportista',
    'E-commerce',
  ]
  if (!rolesPermitidos.includes(rol)) {
    console.warn('[validateUserStatusAndRole] unauthorized rol', { uid, rol })
    return {
      valid: false,
      reason: 'invalid_role',
      internalDetail: `rol=${rol}`,
    }
  }

  return {
    valid: true,
    message: 'Usuario válido',
  }
}

/**
 * Update user data
 * @param {string} uid - User UID
 * @param {Object} userData - Updated user data (name, defaultStore, rol)
 * @param {string} currentUserRole - Rol del usuario actual que realiza la operación (opcional)
 * @param {Object} actor - Usuario autenticado (uid, email, name)
 * @returns {Promise<Object>} Result of update operation
 */
export const updateUser = async (uid, userData, currentUserRole = null, actor = null) => {
  try {
    const actorValidation = validateUserActor(actor)
    if (!actorValidation.valid) {
      return {
        success: false,
        error: actorValidation.error,
      }
    }

    const normalizedActor = actorValidation.actor

    // Validar que solo un Administrador puede asignar el rol de Administrador
    if (userData.rol === 'Administrador' && currentUserRole !== 'Administrador') {
      return {
        success: false,
        error:
          'No tienes permisos para asignar el rol de Administrador. Solo un Administrador puede asignar este rol.',
      }
    }

    // Si se está intentando cambiar el rol de un Administrador y el usuario actual no es Administrador,
    // obtener el usuario actual de la BD para verificar su rol
    if (userData.rol !== undefined && userData.rol !== 'Administrador') {
      const currentUserData = await getUserByUID(uid)
      if (
        currentUserData.success &&
        currentUserData.data?.rol === 'Administrador' &&
        currentUserRole !== 'Administrador'
      ) {
        return {
          success: false,
          error:
            'No tienes permisos para cambiar el rol de un usuario Administrador. Solo un Administrador puede modificar este rol.',
        }
      }
    }

    const userRef = ref(database, `projects/superkomprasBackoffice/users/${uid}`)
    const existingUser = await getUserByUID(uid)

    if (!existingUser.success || !existingUser.data) {
      return {
        success: false,
        error: 'Usuario no encontrado',
      }
    }

    const existing = existingUser.data
    const merged = {
      name: userData.name !== undefined ? userData.name : existing.name,
      defaultStore: userData.defaultStore !== undefined ? userData.defaultStore : existing.defaultStore,
      rol: userData.rol !== undefined ? userData.rol : existing.rol,
      status: userData.status !== undefined ? userData.status : existing.status,
      email: existing.email,
    }

    const beforeState = normalizeUserForAudit(existing)
    const afterState = normalizeUserForAudit(merged)
    const changes = computeUserChanges(beforeState, afterState)

    if (Object.keys(changes).length === 0) {
      return {
        success: true,
        message: 'Usuario actualizado correctamente',
      }
    }

    const now = getTimestamp()
    const updates = {
      updatedAt: now,
      updatedBy: normalizedActor,
    }

    if (userData.name !== undefined) updates.name = userData.name
    if (userData.defaultStore !== undefined) updates.defaultStore = userData.defaultStore
    if (userData.rol !== undefined) updates.rol = userData.rol
    if (userData.status !== undefined) updates.status = userData.status

    const auditLogRef = push(
      ref(database, `projects/superkomprasBackoffice/users/${uid}/auditLog`)
    )
    updates[`auditLog/${auditLogRef.key}`] = buildUserAuditEntry(
      'updated',
      normalizedActor,
      changes,
      null,
      now
    )

    await update(userRef, updates)

    return {
      success: true,
      message: 'Usuario actualizado correctamente',
    }
  } catch (error) {
    console.error('Error in updateUser:', error)
    return {
      success: false,
      error: error.message || 'Error al actualizar usuario',
    }
  }
}

/**
 * Delete user from Realtime Database
 * @param {string} uid - User UID
 * @param {Object} actor - Usuario autenticado que ejecuta la eliminación
 * @returns {Promise<Object>} Result of delete operation
 */
export const deleteUser = async (uid, actor = null) => {
  try {
    if (HIGH_IMPACT_DELETIONS_DISABLED) {
      return getHighImpactDeletionBlock('eliminar usuario')
    }

    const existingUser = await getUserByUID(uid)
    if (!existingUser.success || !existingUser.data) {
      return {
        success: false,
        error: 'Usuario no encontrado',
      }
    }

    const logResult = await writeUserDeleteLog(uid, existingUser.data, actor)
    if (!logResult.success) {
      return logResult
    }

    const userRef = ref(database, `${USERS_BASE_PATH}/${uid}`)
    await remove(userRef)

    return {
      success: true,
      message: 'Usuario eliminado correctamente',
    }
  } catch (error) {
    console.error('Error in deleteUser:', error)
    return {
      success: false,
      error: error.message || 'Error al eliminar usuario',
    }
  }
}

/**
 * Record admin-initiated password reset email in user audit log.
 * @param {string} uid - Target user UID
 * @param {Object} actor - Administrator who triggered the reset
 * @returns {Promise<Object>}
 */
export const recordPasswordResetSent = async (uid, actor) => {
  try {
    const existingUser = await getUserByUID(uid)
    if (!existingUser.success || !existingUser.data) {
      return {
        success: false,
        error: 'Usuario no encontrado',
      }
    }

    return appendUserAuditEvent(uid, 'password_reset_sent', actor, {
      meta: {
        email: existingUser.data.email || '',
      },
    })
  } catch (error) {
    console.error('Error in recordPasswordResetSent:', error)
    return {
      success: false,
      error: error.message || 'Error al registrar auditoría de reset de contraseña',
    }
  }
}

/**
 * Suspend user (change status to "inactivo")
 * @param {string} uid - User UID
 * @returns {Promise<Object>} Result of suspend operation
 */
export const suspendUser = async uid => {
  try {
    const userRef = ref(database, `projects/superkomprasBackoffice/users/${uid}`)
    await update(userRef, {
      status: 'inactivo',
      updatedAt: getTimestamp(),
    })

    return {
      success: true,
      message: 'Usuario suspendido correctamente',
    }
  } catch (error) {
    console.error('Error in suspendUser:', error)
    return {
      success: false,
      error: error.message || 'Error al suspender usuario',
    }
  }
}

/**
 * Activate user (change status to "activo")
 * @param {string} uid - User UID
 * @returns {Promise<Object>} Result of activate operation
 */
export const activateUser = async uid => {
  try {
    const userRef = ref(database, `projects/superkomprasBackoffice/users/${uid}`)
    await update(userRef, {
      status: 'activo',
      updatedAt: getTimestamp(),
    })

    return {
      success: true,
      message: 'Usuario activado correctamente',
    }
  } catch (error) {
    console.error('Error in activateUser:', error)
    return {
      success: false,
      error: error.message || 'Error al activar usuario',
    }
  }
}

/**
 * Get all stores (Tiendas) from Realtime Database
 * @returns {Promise<Object>} All stores data with sucursal and division
 */
export const getAllStores = async () => {
  try {
    const storesRef = ref(database, STORES_BASE_PATH)
    const snapshot = await get(storesRef)

    if (snapshot.exists()) {
      const storesData = snapshot.val()
      // Convertir objeto a array y mantener sucursal y division
      const storesArray = Object.keys(storesData).map(storeId => ({
        id: storeId,
        sucursal: storesData[storeId].sucursal || '',
        division: storesData[storeId].division || '',
      }))

      return {
        success: true,
        data: storesArray,
      }
    } else {
      return {
        success: true,
        data: [],
      }
    }
  } catch (error) {
    console.error('Error in getAllStores:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener tiendas',
      data: [],
    }
  }
}

/**
 * Get store name (sucursal) by division
 * @param {string} division - Division code
 * @returns {Promise<Object>} Store name or null if not found
 */
export const getStoreNameByDivision = async division => {
  try {
    if (!division) {
      return {
        success: true,
        data: null,
      }
    }

    const cleanDivision = division.trim()

    if (!cleanDivision) {
      return {
        success: true,
        data: null,
      }
    }

    const storeRef = ref(database, `${STORES_BASE_PATH}/${cleanDivision}`)
    const storeSnapshot = await get(storeRef)

    if (storeSnapshot.exists()) {
      const storeData = storeSnapshot.val()

      return {
        success: true,
        data: storeData?.sucursal || null,
      }
    }

    const storesRef = ref(database, STORES_BASE_PATH)
    const snapshot = await get(storesRef)

    if (!snapshot.exists()) {
      return {
        success: true,
        data: null,
      }
    }

    const storesData = snapshot.val()

    for (const storeId in storesData) {
      const store = storesData[storeId]

      if (storeId === cleanDivision || store?.division === cleanDivision) {
        return {
          success: true,
          data: store?.sucursal || null,
        }
      }
    }

    return {
      success: true,
      data: null,
    }
  } catch (error) {
    console.error('Error in getStoreNameByDivision:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener nombre de tienda',
      data: null,
    }
  }
}

/**
 * Create user in Realtime Database
 * @param {string} uid - User UID from Firebase Authentication
 * @param {Object} userData - User data (email, name, defaultStore, rol, status, hashedPassword)
 * @returns {Promise<Object>} Result of create operation
 */
/**
 * Create user in database
 * @param {string} uid - User UID
 * @param {Object} userData - User data to save
 * @param {string} currentUserRole - Rol del usuario actual que realiza la operación (opcional)
 * @param {Object} actor - Usuario autenticado que da de alta (uid, email, name)
 * @returns {Promise<Object>} Result of create operation
 */
export const createUserInDatabase = async (uid, userData, currentUserRole = null, actor = null) => {
  try {
    const actorValidation = validateUserActor(actor)
    if (!actorValidation.valid) {
      return {
        success: false,
        error: actorValidation.error,
      }
    }

    const normalizedActor = actorValidation.actor

    // Validar que solo un Administrador puede crear usuarios con rol de Administrador
    if (userData.rol === 'Administrador' && currentUserRole !== 'Administrador') {
      return {
        success: false,
        error:
          'No tienes permisos para crear usuarios con el rol de Administrador. Solo un Administrador puede asignar este rol.',
      }
    }

    const userRef = ref(database, `projects/superkomprasBackoffice/users/${uid}`)

    // Verificar si el usuario ya existe para preservar createdAt
    const existingUser = await get(userRef)
    const existingCreatedAt = existingUser.exists() ? existingUser.val()?.createdAt : null

    const timestamp = getTimestamp()
    const auditSnapshot = normalizeUserForAudit({
      name: userData.name,
      email: userData.email,
      rol: userData.rol,
      status: userData.status || 'activo',
      defaultStore: userData.defaultStore,
    })
    const auditLogRef = push(ref(database, `projects/superkomprasBackoffice/users/${uid}/auditLog`))
    const auditLogKey = auditLogRef.key

    const userDataToSave = {
      email: userData.email,
      name: userData.name || '',
      defaultStore: userData.defaultStore || '',
      rol: userData.rol || '',
      status: userData.status || 'activo',
      hashedPassword: userData.hashedPassword || '',
      createdAt: existingCreatedAt || timestamp,
      createdBy: existingUser.exists() && existingUser.val()?.createdBy
        ? existingUser.val().createdBy
        : normalizedActor,
      updatedAt: timestamp,
      updatedBy: normalizedActor,
      auditLog: {
        ...(existingUser.exists() && existingUser.val()?.auditLog
          ? existingUser.val().auditLog
          : {}),
        ...(existingUser.exists()
          ? {}
          : {
              [auditLogKey]: buildUserAuditEntry(
                'created',
                normalizedActor,
                null,
                auditSnapshot,
                timestamp
              ),
            }),
      },
    }

    // Verificar que hashedPassword se proporcionó
    if (!userData.hashedPassword || userData.hashedPassword === '') {
      console.warn('⚠️ Advertencia: hashedPassword no se proporcionó al crear usuario')
    } else {
      console.log('✅ hashedPassword proporcionado, longitud:', userData.hashedPassword.length)
    }

    // Si hashedPassword no está presente pero se proporcionó un password, hashearlo
    if (!userData.hashedPassword && userData.password) {
      const bcrypt = await import('bcryptjs')
      const saltRounds = 10
      userDataToSave.hashedPassword = await bcrypt.default.hash(userData.password, saltRounds)
      console.log(
        '✅ Hash generado en createUserInDatabase, longitud:',
        userDataToSave.hashedPassword.length
      )
    }

    console.log(
      'Guardando usuario en BD con hashedPassword:',
      userDataToSave.hashedPassword
        ? 'SÍ (longitud: ' + userDataToSave.hashedPassword.length + ')'
        : 'NO'
    )

    await set(userRef, userDataToSave)

    return {
      success: true,
      message: 'Usuario creado correctamente en la base de datos',
    }
  } catch (error) {
    console.error('Error in createUserInDatabase:', error)
    return {
      success: false,
      error: error.message || 'Error al crear usuario en la base de datos',
    }
  }
}

/**
 * Sync password hash in database with current password
 * This is useful when password was reset via Firebase Auth link
 * @param {string} uid - User UID
 * @param {string} currentPassword - Current password from login
 * @param {string} existingHashedPassword - Existing hashedPassword in database
 * @returns {Promise<Object>} Sync result
 */
export const syncPasswordHash = async (uid, currentPassword, existingHashedPassword) => {
  try {
    // Si no hay hashedPassword o está vacío, actualizarlo
    if (!existingHashedPassword || existingHashedPassword === '') {
      const saltRounds = 10
      const hashedPassword = await bcrypt.hash(currentPassword, saltRounds)

      const userRef = ref(database, `projects/superkomprasBackoffice/users/${uid}`)
      await update(userRef, {
        hashedPassword: hashedPassword,
        updatedAt: getTimestamp(),
      })

      console.log('✅ hashedPassword sincronizado después de login (estaba vacío)')
      return {
        success: true,
        synced: true,
      }
    }

    // Si hay hashedPassword, verificar si coincide con la contraseña actual
    try {
      const isMatch = await bcrypt.compare(currentPassword, existingHashedPassword)

      // Si no coincide, actualizar el hash (caso de reset de contraseña)
      if (!isMatch) {
        const saltRounds = 10
        const hashedPassword = await bcrypt.hash(currentPassword, saltRounds)

        const userRef = ref(database, `projects/superkomprasBackoffice/users/${uid}`)
        await update(userRef, {
          hashedPassword: hashedPassword,
          updatedAt: getTimestamp(),
        })

        console.log('✅ hashedPassword sincronizado después de login (no coincidía)')
        return {
          success: true,
          synced: true,
        }
      }
    } catch (bcryptError) {
      // Si hay error al comparar (hash corrupto o formato incorrecto), actualizar
      console.warn('Error al comparar hash, actualizando:', bcryptError)
      const saltRounds = 10
      const hashedPassword = await bcrypt.hash(currentPassword, saltRounds)

      const userRef = ref(database, `projects/superkomprasBackoffice/users/${uid}`)
      await update(userRef, {
        hashedPassword: hashedPassword,
        updatedAt: getTimestamp(),
      })

      console.log('✅ hashedPassword sincronizado después de login (error en comparación)')
      return {
        success: true,
        synced: true,
      }
    }

    // Si coincide, no es necesario actualizar
    return {
      success: true,
      synced: false,
    }
  } catch (error) {
    console.error('Error in syncPasswordHash:', error)
    // No bloqueamos el login si falla la sincronización
    return {
      success: false,
      error: error.message || 'Error al sincronizar hashedPassword',
      synced: false,
    }
  }
}

/**
 * Validate current password by comparing with hashedPassword in database
 * @param {string} uid - User UID
 * @param {string} currentPassword - Current password to validate
 * @returns {Promise<Object>} Validation result
 */
export const validateCurrentPassword = async (uid, currentPassword) => {
  try {
    // Obtener usuario de la base de datos
    const userResult = await getUserByUID(uid)

    if (!userResult.success || !userResult.data) {
      return {
        success: false,
        error: 'Usuario no encontrado',
      }
    }

    const userData = userResult.data
    const hashedPassword = userData.hashedPassword

    // Si no hay hashedPassword guardado, validar usando Firebase Auth
    if (!hashedPassword || hashedPassword === '') {
      // En este caso, validamos usando Firebase Authentication
      // Esta función debería llamarse desde el componente después de reauthenticateUser
      return {
        success: true,
        valid: true,
        message: 'Contraseña válida',
      }
    }

    // Comparar contraseña actual con hashedPassword
    const isMatch = await bcrypt.compare(currentPassword, hashedPassword)

    if (isMatch) {
      return {
        success: true,
        valid: true,
        message: 'Contraseña válida',
      }
    } else {
      return {
        success: true,
        valid: false,
        error: 'La contraseña actual es incorrecta',
      }
    }
  } catch (error) {
    console.error('Error in validateCurrentPassword:', error)
    return {
      success: false,
      error: error.message || 'Error al validar contraseña',
    }
  }
}

/**
 * Update hashedPassword in Realtime Database
 * @param {string} uid - User UID
 * @param {string} newPassword - New password to hash
 * @param {Object} actor - Usuario autenticado que cambia la contraseña
 * @returns {Promise<Object>} Result of update operation
 */
export const updateUserPasswordHash = async (uid, newPassword, actor = null) => {
  try {
    const actorValidation = validateUserActor(actor)
    if (!actorValidation.valid) {
      return {
        success: false,
        error: actorValidation.error,
      }
    }

    // Generar hash de la nueva contraseña
    const saltRounds = 10
    const hashedPassword = await bcrypt.hash(newPassword, saltRounds)

    const now = getTimestamp()
    const normalizedActor = actorValidation.actor
    const auditLogRef = push(ref(database, `${USERS_BASE_PATH}/${uid}/auditLog`))

    // Actualizar hashedPassword en la base de datos (sin registrar la contraseña en auditLog)
    const userRef = ref(database, `${USERS_BASE_PATH}/${uid}`)
    await update(userRef, {
      hashedPassword: hashedPassword,
      updatedAt: now,
      updatedBy: normalizedActor,
      [`auditLog/${auditLogRef.key}`]: buildUserAuditEntry(
        'password_changed',
        normalizedActor,
        null,
        null,
        now,
        { selfService: normalizedActor.uid === uid }
      ),
    })

    return {
      success: true,
      message: 'Contraseña actualizada correctamente en la base de datos',
    }
  } catch (error) {
    console.error('Error in updateUserPasswordHash:', error)
    return {
      success: false,
      error: error.message || 'Error al actualizar contraseña en la base de datos',
    }
  }
}
